import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { ControlsOf, FormGroupType } from '@app/core/utils/formgroup-type';
import { AddressDto } from '@app/api/nts';
import { AddressResultType, AddressValidationOutcome } from '@app/api/dmc';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { SearchConfig } from '@shared/components/input/search/search-config.interface';
import { AutocompleteService } from '@app/core/services/autocomplete.service';
import { Observable, of } from 'rxjs';
import { SearchResultCollection } from '@shared/components/input/search/search-result.interface';
import { map } from 'rxjs/operators';
import { DarSearchResult, mapDarResults } from '@app/core/utils/map-dar-results';
import { patchAddress } from '@app/core/utils/patch-address';
import { ToastService } from '@app/core/services/toast.service';
import { AlertService } from '@app/core/services/alert.service';
import { SearchComponent } from '@shared/components/input/search/search.component';
import { debounce } from '@app/core/utils/debounce';

@Component({
  selector: 'app-address',
  imports: [
    ReactiveFormsModule,
    InputDirective,
    InputGroupComponent,
    LabelDirective,
    TranslatePipe,
    NgClass,
    SearchComponent
  ],
  templateUrl: './address.component.html',
  styleUrl: './address.component.css'
})
export class AddressComponent implements OnInit, OnChanges {
  public form: FormGroupType<AddressDto> = new FormGroup<ControlsOf<AddressDto>>({
    streetName: new FormControl<string | null>({ value: null, disabled: true }),
    streetCode: new FormControl<string | null>({ value: null, disabled: true }),
    buildingNumber: new FormControl<string | null>({ value: null, disabled: true }),
    floorIdentification: new FormControl<string | null>({ value: null, disabled: true }),
    roomIdentification: new FormControl<string | null>({ value: null, disabled: true }),
    subDivisionName: new FormControl<string | null>({ value: null, disabled: true }),
    postalCode: new FormControl<string | null>({ value: null, disabled: true }),
    cityName: new FormControl<string | null>({ value: null, disabled: true }),
    municipalityCode: new FormControl<string | null>({ value: null, disabled: true }),
    country: new FormControl<string | null>({ value: null, disabled: true }),
    darReference: new FormControl<string | null>({ value: null, disabled: true }),
  });

  @Input() public address?: AddressDto | null = {};
  @Output() public addressChange: EventEmitter<AddressDto> = new EventEmitter();

  @Input() public layout: 'linear' | 'horizontal' = 'linear';

  /** Search Config **/
  public searchConfig: SearchConfig = {
    hotkeys: false,
    standalone: true,
    autoFocus: false,
    navigateUrl: undefined,
    callBackFunction: (value: string) => this.searchQuery(value),
  }

  public advancedInput: boolean = false;
  public addressFetched: boolean = false;

  constructor(
    private readonly autocompleteService: AutocompleteService,
    private readonly toastService: ToastService,
    private readonly alertService: AlertService,
  ) { }

  public ngOnInit(): void {
    // The address fields stay disabled while searching, and a disabled control is
    // omitted from FormGroup.value, so the raw value is needed to emit the address.
    const emitDebounced = debounce(() => {
      this.addressChange.emit(this.form.getRawValue());
    }, 300);

    this.form.valueChanges.subscribe(emitDebounced);
  }

  public ngOnChanges(): void {
    if (this.address) {
      this.form.patchValue(this.address);
    }
  }

  /** Search Query **/
  public searchQuery(value: string): Observable<SearchResultCollection[]> {
    if (value.length > 2) {
      return this.autocompleteService.searchAddress(value)
        .pipe(
          map(results => mapDarResults(
            results,
            'address',
            text => this.autocompleteService.searchAddress(text),
            id => this.autocompleteService.getByAccessAddressId(id),
          )),
        )
    }
    return of([]);
  }

  public addressSelected(event: DarSearchResult): void {
    if (event.id) {
      const type = event.type === AddressResultType.AccessAddress ? 'access' : 'address';
      this.autocompleteService.getById(event.id, type).subscribe((addr) => {
        /** Patch Address **/
        patchAddress(this.form, addr);

        /** Adress Fetched **/
        this.addressFetched = true;
      })
    }
  }

  public sanitizeAddress(): void {
    this.form.markAsTouched();

    if (!this.form.valid) return;

    const addr = [
      this.form.get('streetName')?.value,
      this.form.get('buildingNumber')?.value,
      this.form.get('postalCode')?.value,
      this.form.get('cityName')?.value,
    ].filter(Boolean).join(' ').trim();

    this.autocompleteService.validateAddress(addr).subscribe((result) => {
      if (result.outcome === AddressValidationOutcome.Validated && result.address) {
        patchAddress(this.form, result.address);
        this.toastService.show('address.addresssanitized');
        return;
      }

      this.alertService.open(
        this.layout === 'linear' ? 'modal' : 'header',
        'danger',
        AddressComponent.validationMessage(result.outcome)
      );
    });
  }

  private static validationMessage(outcome: AddressValidationOutcome): string {
    switch (outcome) {
      case AddressValidationOutcome.MultipleMatches:
        return 'address.multiplematches';
      case AddressValidationOutcome.NotFound:
        return 'address.notfound';
      default:
        return 'address.notunique';
    }
  }

  public activateSearch(): void {
    this.advancedInput = false;
    this.addressFetched = false;

    this.form.disable();
    this.form.get('streetName')?.removeValidators(Validators.required);
    this.form.get('buildingNumber')?.removeValidators(Validators.required);
    this.form.get('postalCode')?.removeValidators(Validators.required);
    this.form.get('country')?.removeValidators(Validators.required);
  }

  public activateAdvanced(): void {
    this.advancedInput = true;
    this.addressFetched = false;

    this.form.enable();
    this.form.get('streetName')?.setValidators(Validators.required);
    this.form.get('buildingNumber')?.setValidators(Validators.required);
    this.form.get('postalCode')?.setValidators(Validators.required);
    this.form.get('country')?.setValidators(Validators.required);
  }
}
